/** @jsx jsx */
import { useContext, useMemo } from 'react';
import { jsx } from '@emotion/core';
import { useFormikContext } from 'formik';

import { ICustomTheme } from 'interfaces';

import {
  StyledNavButton,
  StyledContainer,
  StyledArrowBackIcon,
  StyledDoneIcon,
  StyledArrowForwardIcon,
} from './styles';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { DeclarationContext } from 'components/DeclarationForm';
import {
  IForm,
  IFormProduct,
  StepsType,
} from 'components/DeclarationForm/interfaces';
import { STEPS } from 'components/DeclarationForm/constants';

export const NavButtons = ({
  step,
  selectedProducts,
  scrollTop,
}: {
  step: StepsType;
  selectedProducts: IFormProduct[];
  scrollTop?: () => void;
}) => {
  const {
    isFirstStep,
    goPrevStep,
    goNextStep,
    setOpenDeclarationConfirmationDialog,
  } = useContext(DeclarationContext);
  const { touched, errors } = useFormikContext<IForm>();

  const theme: ICustomTheme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const isStepTouched = step === STEPS.summary || !!touched[step];
  const isStepErrored = useMemo(() => {
    if (step === STEPS.summary) {
      return false;
    }

    if (step !== STEPS.products) {
      return !!errors[step];
    }

    return !selectedProducts?.length || !!errors[step];
  }, [errors, step, selectedProducts]);

  const resolveScrollTop = (scrollTop: () => void) => {
    return new Promise((resolve) => {
      resolve(scrollTop());
    });
  };

  const handleChangeStepWithScrollTop = async (
    scrollTop: (() => void) | undefined,
    gotToStepAction: () => void,
  ) => {
    scrollTop && (await resolveScrollTop(scrollTop));
    return gotToStepAction();
  };

  return (
    <StyledContainer isMobile={isMobile}>
      {!isFirstStep && (
        <StyledNavButton
          onClick={() => handleChangeStepWithScrollTop(scrollTop, goPrevStep)}
          variant="contained"
          type="button"
          disableElevation
          backgroundColor={theme.colors.grey.main}
          isMobile={isMobile}
        >
          <StyledArrowBackIcon />
        </StyledNavButton>
      )}
      <StyledNavButton
        disabled={!isStepTouched || isStepErrored}
        onClick={() =>
          step === STEPS.summary
            ? setOpenDeclarationConfirmationDialog(true)
            : handleChangeStepWithScrollTop(scrollTop, goNextStep)
        }
        variant="contained"
        type="button"
        disableElevation
        backgroundColor={theme.colors.paletteFromBO.primary}
        isMobile={isMobile}
      >
        {step === STEPS.summary ? (
          <StyledDoneIcon />
        ) : (
          <StyledArrowForwardIcon />
        )}
      </StyledNavButton>
    </StyledContainer>
  );
};
